/**
 * ampEmailExport — produce the AMP-for-Email (amp4email) version of an email,
 * i.e. the `text/x-amp-html` MIME part. Story blocks become an interactive
 * <amp-carousel> of the story's page images; other blocks render amp-safe.
 *
 * IMPORTANT amp4email constraints handled here:
 *  - doctype `<html ⚡4email>` + required v0.js + amp4email-boilerplate.
 *  - NO inline `style` attributes allowed → all styling goes in <style amp-custom>
 *    via per-block classes; rich-text HTML has its style/on* attributes stripped.
 *  - <img> is not allowed → <amp-img layout="responsive"> with explicit dims.
 *  - amp-carousel needs its custom-element script (added only when a story exists).
 *
 * To actually deliver this interactively, the send path must attach it as the
 * text/x-amp-html part alongside the text/html fallback, and the sender must be
 * AMP-whitelisted with Gmail/Yahoo. This module only produces the code.
 */
import { wrapConditional } from './emailConditions'
import { countdownImgUrl, countdownImgWidth, formSubmitEndpoint } from './htmlExport'
import { PRIMARY_BG } from '../../../../theme/brand'

// Strip everything amp4email forbids from rich-text HTML (inline styles, event
// handlers, script/style tags). Keeps basic inline formatting + links.
function ampSafeHtml(html) {
  return String(html || '')
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    .replace(/\sstyle="[^"]*"/gi, '')
    .replace(/\sstyle='[^']*'/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '')
}

const esc = s => String(s == null ? '' : s).replace(/"/g, '&quot;')
const px = (v, d = 0) => Number(v == null ? d : v)

// Each block → { markup, css } where css is scoped by a unique class.
function ampBlock(block, id) {
  const p = block.props || {}
  const c = `b${id}`
  const padCss = `padding:${px(p.paddingTop)}px ${px(p.paddingRight)}px ${px(p.paddingBottom)}px ${px(p.paddingLeft)}px;`
  const bg = p.backgroundColor ? `background-color:${p.backgroundColor};` : ''

  switch (block.type) {
    case 'story': {
      const url = esc(p.url || '#')
      const slides = (p.slides && p.slides.length ? p.slides : (p.poster ? [{ img: p.poster, alt: p.storyTitle }] : []))
      if (!slides.length) return { markup: '', css: '' }
      // 9:16 story ratio; responsive amp-img inside amp-carousel slides.
      const items = slides.map(s => (
        `<a href="${url}" target="_blank" rel="noopener"><amp-img src="${esc(s.img)}" alt="${esc(s.alt || p.storyTitle)}" width="1080" height="1920" layout="responsive"></amp-img></a>`
      )).join('')
      const cta = p.showCta
        ? `<div class="${c}-cta"><a href="${url}" target="_blank" rel="noopener">&#9658; ${esc(p.ctaLabel || 'View Story')}</a></div>` : ''
      // ET_023: the play badge was dropped entirely from the AMP part, so in Gmail —
      // the client that actually renders this part — it never appeared however the
      // switch was set. Emitted in the same caption bar as the HTML part and the
      // canvas, so all three agree.
      const capParts = []
      if (p.showPlayBadge) capParts.push('<span class="' + c + '-b">&#9658;</span>')
      if (p.showTitle && p.storyTitle) capParts.push(`<span>${esc(p.storyTitle)}</span>`)
      const title = capParts.length ? `<div class="${c}-t">${capParts.join(' ')}</div>` : ''
      const auto = p.autoplay !== false
      const delayMs = Math.max(1200, Math.round((Number(p.autoplayDelay) || 3.5) * 1000))
      const carAttrs = `type="slides" loop${auto ? ` autoplay delay="${delayMs}"` : ''}`
      const markup = `<div class="${c}">
  <amp-carousel width="1080" height="1920" layout="responsive" ${carAttrs}>${items}</amp-carousel>
  ${title}${cta}
</div>`
      // The caption bar is sized to the carousel and sits flush under it, sharing the
      // bottom corners — same shape as renderStory()'s <td> caption and the canvas.
      const capW = px(p.width, 100) === 100 ? 300 : px(p.width) * 3
      const css = `.${c}{${padCss}${bg}text-align:${p.alignment || 'center'};}
.${c} amp-carousel{max-width:${capW}px;margin:0 auto;border-radius:${px(p.borderRadius, 12)}px ${px(p.borderRadius, 12)}px ${title ? '0 0' : `${px(p.borderRadius, 12)}px ${px(p.borderRadius, 12)}px`};overflow:hidden;}
.${c}-t{max-width:${capW}px;margin:0 auto;box-sizing:border-box;text-align:left;background:#1f2937;color:#ffffff;font-size:14px;font-weight:700;padding:9px 12px;border-radius:0 0 ${px(p.borderRadius, 12)}px ${px(p.borderRadius, 12)}px;}
.${c}-b{color:#ffffff;font-size:12px;}
.${c}-cta{padding-top:10px;}
.${c}-cta a{display:inline-block;background:${p.ctaBackgroundColor || PRIMARY_BG};color:${p.ctaTextColor || '#fff'};text-decoration:none;font-weight:700;font-size:14px;padding:11px 22px;border-radius:8px;}`
      return { markup, css }
    }

    case 'ampForm': {
      const accent = p.accentColor || PRIMARY_BG
      // Same endpoint the text/html part posts to (see formSubmitEndpoint) so one
      // backend handler serves both parts. It is always absolute https, which
      // action-xhr requires — the amp4email validator rejects a relative one, and an
      // invalid AMP document makes the client discard the ENTIRE AMP part and silently
      // fall back to text/html.
      const endpoint = formSubmitEndpoint()
      const fieldHtml = (p.fields || []).map(f => {
        const req = f.required ? ' required' : ''
        const nm = esc(f.name || f.id)
        const lab = `<label class="${c}-l">${esc(f.label)}</label>`
        if (f.type === 'textarea') return `<div class="${c}-f">${lab}<textarea name="${nm}"${req}></textarea></div>`
        if (f.type === 'select') return `<div class="${c}-f">${lab}<select name="${nm}"${req}>${(f.options || ['Option 1']).map(o => `<option value="${esc(o)}">${esc(o)}</option>`).join('')}</select></div>`
        if (f.type === 'rating') return `<div class="${c}-f">${lab}<div class="${c}-r">${[1, 2, 3, 4, 5].map(n => `<label class="${c}-rb"><input type="radio" name="${nm}" value="${n}"${n === 1 ? req : ''}>${n}</label>`).join('')}</div></div>`
        return `<div class="${c}-f">${lab}<input type="${f.type === 'email' ? 'email' : 'text'}" name="${nm}"${req}></div>`
      }).join('')
      const markup = `<div class="${c}">
  <div class="${c}-card">
    ${p.title ? `<div class="${c}-h">${esc(p.title)}</div>` : ''}
    ${p.intro ? `<div class="${c}-i">${esc(p.intro)}</div>` : ''}
    <form method="post" action-xhr="${esc(endpoint)}">
      ${fieldHtml}
      <input type="submit" value="${esc(p.submitLabel || 'Submit')}" class="${c}-btn">
      <div submit-success><template type="amp-mustache"><div class="${c}-ok">${esc(p.successMessage || 'Thank you!')}</div></template></div>
      <div submit-error><template type="amp-mustache"><div class="${c}-err">Something went wrong — please try again.</div></template></div>
    </form>
  </div>
</div>`
      // ET_028: the AMP part had no card surface at all, so the form floated on the
      // block background — a third different look. Mirrors the HTML part's card now.
      const card = p.cardBackground || '#ffffff'
      const css = `.${c}{${padCss}${bg}}
.${c}-card{background:${card};border:1px solid ${accent}33;border-radius:${px(p.borderRadius, 12)}px;padding:18px;}
.${c}-h{font-size:18px;font-weight:800;color:#111827;}
.${c}-i{font-size:13px;color:#6B7280;margin:4px 0 12px;}
.${c}-f{margin:0 0 12px;}
.${c}-l{display:block;font-size:14px;font-weight:600;color:#374151;margin-bottom:4px;}
.${c} input[type=text],.${c} input[type=email],.${c} textarea,.${c} select{width:100%;box-sizing:border-box;border:1px solid #d1d5db;border-radius:8px;padding:9px 11px;font-size:14px;}
.${c} textarea{min-height:64px;}
.${c}-r{display:flex;gap:8px;}
.${c}-rb{display:inline-flex;align-items:center;gap:4px;font-size:14px;color:#374151;}
.${c}-btn{background:${accent};color:#fff;border:0;border-radius:8px;padding:11px 22px;font-size:14px;font-weight:700;cursor:pointer;}
.${c}-ok{color:#16a34a;font-weight:700;}
.${c}-err{color:#dc2626;font-weight:700;}`
      return { markup, css }
    }

    case 'heading': {
      const tag = /^h[1-6]$/.test(p.tag) ? p.tag : 'h2'
      return {
        markup: `<${tag} class="${c}">${ampSafeHtml(p.content)}</${tag}>`,
        css: `.${c}{margin:0;${padCss}${bg}font-size:${px(p.fontSize, 24)}px;font-weight:${p.fontWeight || 700};color:${p.color || '#000'};text-align:${p.alignment || 'left'};line-height:1.3;}`,
      }
    }
    case 'text': {
      return {
        markup: `<div class="${c}">${ampSafeHtml(p.content)}</div>`,
        css: `.${c}{${padCss}${bg}font-size:${px(p.fontSize, 14)}px;color:${p.color || '#333'};text-align:${p.alignment || 'left'};line-height:${p.lineHeight || 1.6};}`,
      }
    }
    case 'button': {
      return {
        markup: `<div class="${c}"><a href="${esc(p.url || '#')}" target="_blank" rel="noopener">${esc(p.label)}</a></div>`,
        css: `.${c}{${padCss}text-align:${p.alignment || 'center'};}
.${c} a{display:inline-block;background:${p.backgroundColor || '#2563eb'};color:${p.textColor || '#fff'};text-decoration:none;font-size:${px(p.fontSize, 16)}px;font-weight:${p.fontWeight || 600};padding:${px(p.buttonPaddingV, 14)}px ${px(p.buttonPaddingH, 32)}px;border-radius:${px(p.borderRadius, 8)}px;}`,
      }
    }
    case 'image': {
      if (!p.src) return { markup: '', css: '' }
      const inner = `<amp-img src="${esc(p.src)}" alt="${esc(p.alt)}" width="600" height="400" layout="responsive"></amp-img>`
      const wrapped = p.link ? `<a href="${esc(p.link)}" target="_blank" rel="noopener">${inner}</a>` : inner
      return {
        markup: `<div class="${c}">${wrapped}</div>`,
        css: `.${c}{${padCss}${bg}text-align:${p.alignment || 'center'};}
.${c} amp-img{max-width:${px(p.width, 100)}%;margin:0 auto;border-radius:${px(p.borderRadius)}px;overflow:hidden;}`,
      }
    }
    case 'countdown': {
      // Same live GIF as the HTML part, rendered via amp-img (amp4email forbids <img>).
      const w = countdownImgWidth(p)
      const h = Math.round(w * (p.showLabels ? 0.27 : 0.2))
      const inner = `<amp-img src="${esc(countdownImgUrl(p))}" alt="Countdown" width="${w}" height="${h}" layout="intrinsic"></amp-img>`
      const wrapped = p.link ? `<a href="${esc(p.link)}" target="_blank" rel="noopener">${inner}</a>` : inner
      return {
        markup: `<div class="${c}">${wrapped}</div>`,
        css: `.${c}{${padCss}${bg}text-align:${p.alignment || 'center'};}
.${c} amp-img{max-width:100%;margin:0 auto;}`,
      }
    }
    case 'divider': {
      return { markup: `<div class="${c}"></div>`, css: `.${c}{${padCss}border-top:${px(p.thickness, 1)}px ${p.style || 'solid'} ${p.color || '#E5E7EB'};font-size:0;line-height:0;}` }
    }
    case 'spacer': {
      return { markup: `<div class="${c}"></div>`, css: `.${c}{height:${px(p.height, 20)}px;${bg}font-size:0;line-height:0;}` }
    }
    case 'footer': {
      return {
        markup: `<div class="${c}">${ampSafeHtml(p.content)}</div>`,
        css: `.${c}{${padCss}${bg || 'background-color:#f8f8f8;'}font-size:${px(p.fontSize, 12)}px;color:${p.color || '#6B7280'};text-align:${p.alignment || 'center'};}`,
      }
    }
    case 'columns': {
      // amp4email: stack columns vertically (email-safe, valid).
      const defs = p.columnDefs || []
      let sub = ''
      const subCss = []
      let n = id * 100
      defs.forEach(col => (col.blocks || []).forEach(cb => {
        const r = ampBlock(cb, ++n)
        if (r.markup) sub += r.markup
        if (r.css) subCss.push(r.css)
      }))
      return { markup: `<div class="${c}">${sub}</div>`, css: `.${c}{${padCss}${bg}}\n${subCss.join('\n')}` }
    }
    default:
      return { markup: '', css: '' } // headerBar/dropShadow/social/video/html → omitted from AMP part
  }
}

export function blocksToAmpEmail(blocks, settings = {}) {
  const list = Array.isArray(blocks) ? blocks : []
  const bodyParts = []
  const cssParts = []
  list.forEach((blk, i) => {
    const { markup, css } = ampBlock(blk, i + 1)
    if (markup) bodyParts.push(wrapConditional(markup, blk.props && blk.props._condition))
    if (css) cssParts.push(css)
  })
  const serialized = JSON.stringify(list)
  const hasStory = serialized.indexOf('"story"') !== -1
  const hasForm = serialized.indexOf('"ampForm"') !== -1
  const scripts = [
    hasStory && '<script async custom-element="amp-carousel" src="https://cdn.ampproject.org/v0/amp-carousel-0.2.js"></script>',
    hasForm && '<script async custom-element="amp-form" src="https://cdn.ampproject.org/v0/amp-form-0.1.js"></script>',
    hasForm && '<script async custom-template="amp-mustache" src="https://cdn.ampproject.org/v0/amp-mustache-0.2.js"></script>',
  ].filter(Boolean).join('\n')
  const width = px(settings.contentWidth, 600)
  return `<!doctype html>
<html ⚡4email>
<head>
<meta charset="utf-8">
<script async src="https://cdn.ampproject.org/v0.js"></script>
${scripts}
<style amp4email-boilerplate>body{visibility:hidden}</style>
<style amp-custom>
body{margin:0;padding:0;background:${settings.backgroundColor || '#f7f9fc'};font-family:Arial,Helvetica,sans-serif;}
.amp-wrap{max-width:${width}px;margin:0 auto;background:${settings.contentBackground || '#ffffff'};}
a{color:${settings.linkColor || '#2563eb'};}
${cssParts.join('\n')}
</style>
</head>
<body>
<div class="amp-wrap">
${bodyParts.join('\n')}
</div>
</body>
</html>`
}

export default blocksToAmpEmail
